<template>
    <CModal class="LinkedInAuthorizeModal">
        <article class="flex flex-col items-center gap-7">
            <div class="flex w-full items-center justify-center pt-5">
                <img
                    alt="GROWNi logo"
                    class="block aspect-square size-16 shrink-0 drop-shadow"
                    :height="64"
                    src="@/assets/images/logos/growni-circle.png"
                    :width="64"
                />

                <img
                    alt="LinkedIn logo"
                    class="-ml-2 block aspect-square size-16 shrink-0 drop-shadow"
                    :height="64"
                    src="@/assets/images/logos/linkedin-circle.png"
                    :width="64"
                />
            </div>

            <CHeading class="\!mb-0 max-w-[24ch] text-center" variant="3">
                Synchronizácia s <span class="text-[#0066C8]">LinkedIn</span> účtom
            </CHeading>

            <section class="flex flex-col gap-3 rounded-lg bg-gray-100 dark:bg-subtle p-5">
                <span class="mb-3 text-sm">
                    LinkedIn vyžaduje iba jeden súhlas k celému balíku údajov. GROWNi sa zaväzuje k prístupu iba k
                    nasledujúcim udajom po dobu 365 dní:
                </span>

                <div class="grid grid-cols-2 gap-3">
                    <CFormCheckbox
                        v-for="domain in allDomains"
                        :key="`domain-${domain}`"
                        :disabled="state === 'loading'"
                        :model-value="selectedDomains.has(domain)"
                        @update:model-value="updateDomain(domain, $event)"
                    >
                        <div class="flex flex-col gap-1 pl-1">
                            <span class="text-md font-medium">{{ $t(`linkedInDomains.${domain}.title`, lang) }}</span>
                            <small class="text-sm text-gray-600 dark:text-txt-body">
                                {{ $t(`linkedInDomains.${domain}.description`, lang) }}
                            </small>
                        </div>
                    </CFormCheckbox>
                </div>
            </section>
        </article>

        <template #footer>
            <footer class="-mt-2 flex flex-col items-center gap-4">
                <CButton
                    class="w-full"
                    :disabled="state === 'loading'"
                    :loading="state === 'loading'"
                    size="large"
                    variant="primary"
                    @click="handleSubmit"
                >
                    Súhlasím so synchronizáciou
                </CButton>

                <small class="w-full text-center text-xs text-gray-700 dark:text-txt-body">
                    Toto rozhodnutie môžeš kedykoľvek odvolať alebo upraviť. Odvolanie súhlasu na strane LinkedIn nájdeš
                    v
                    <CButton
                        class="\!min-h-0 \!p-0 text-xs leading-4.5 text-gray-700 dark:text-txt-body"
                        href="https://www.linkedin.com/mypreferences/d/data-sharing-for-permitted-services"
                        target="_blank"
                        variant="link"
                    >
                        nastaveniach
                    </CButton>
                    svojho účtu.
                </small>
            </footer>
        </template>
    </CModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { LINKEDIN_DEFAULT_DOMAINS } from '#shared/constants/linkedIn'
import { EMemberSnapshotDataDomains } from '#shared/types/LinkedIn'
import { LoadingState } from '#shared/types/LoadingState'

const props = defineProps({
    lang: {
        type: String as PropType<'sk' | 'en'>,
        default: 'sk'
    },
    connectionData: {
        type: Object as PropType<any | null>,
        default: null
    },
    onSubmit: {
        type: Function as PropType<(domains: EMemberSnapshotDataDomains[]) => Promise<void>>,
        required: true
    }
})

const currentDomains = props.connectionData?.domains
const selectedDomains = ref<Set<EMemberSnapshotDataDomains>>(new Set(currentDomains || LINKEDIN_DEFAULT_DOMAINS))
const allDomains = Object.values(EMemberSnapshotDataDomains)
const state = ref<LoadingState>('idle')

function updateDomain(domain: EMemberSnapshotDataDomains, value: boolean) {
    if (value) selectedDomains.value.add(domain)
    else selectedDomains.value.delete(domain)
}

async function handleSubmit() {
    state.value = 'loading'

    try {
        const domains = Array.from(selectedDomains.value)
        await props.onSubmit(domains)
        // The onSubmit function handles the redirect to LinkedIn
        // so we don't need to reset the state as the page will navigate away
    } catch (err) {
        console.error(err)
        state.value = 'error'
    }
}
</script>
